import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import BlockRenderer from '../components/blocks/BlockRenderer';
import api from '../lib/api';

/**
 * ============================================================================
 * PublicPage - Rendu des pages CMS publiques
 * ============================================================================
 * 
 * MIGRATION v2.0: Utilise maintenant BlockRenderer officiel
 * 
 * AVANT (LEGACY):
 * - renderBlock() local avec support limité (5 types)
 * - Blocs cassés si type non reconnu
 * - UI différente admin vs public
 * 
 * APRÈS (UNIFIED):
 * - BlockRenderer officiel (12+ types supportés)
 * - Rendu identique admin vs public
 * - Fallback gracieux pour types inconnus
 * 
 * BLOCS SUPPORTÉS:
 * ✅ banner, banner_fixed_centered
 * ✅ carousel_banner
 * ✅ modular_grid
 * ✅ text
 * ✅ feature_grid
 * ✅ product_list
 * ✅ promotion_cards_grid
 * ✅ partner_cards_grid
 * ✅ navigation_cards
 * ✅ thematic_page_card
 * ✅ global_block_reference
 */

const PublicPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPage();
  }, [slug]);

  const loadPage = async () => {
    try {
      setLoading(true);
      setError(null);
      // Remove leading slash from slug for API call
      const apiSlug = slug === 'blog' ? 'dashboard/blog' : (slug.startsWith('/') ? slug.substring(1) : slug);
      const response = await api.get(`/pages/slug/${apiSlug}`);
      setPage(response.data);
    } catch (error) {
      console.error('Error loading page:', error);
      setError(error.response?.status === 404 ? 'Page non trouvée' : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </PublicLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <PublicLayout>
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl font-heading font-bold mb-4">Page non trouvée</h1>
          <p className="text-muted-foreground mb-8">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors"
            data-testid="back-to-home-btn"
          >
            Retour à l'accueil
          </button>
        </div>
      </PublicLayout>
    );
  }

  // Get active blocks sorted by order
  const activeBlocks = (page?.blocks || [])
    .filter(block => block.is_active !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <PublicLayout>
      <div data-testid={`page-${page?.slug}`} className="public-page-content">
        {activeBlocks.length > 0 ? (
          // ========== UNIFIED CMS BLOCKS RENDERING ==========
          // Utilise BlockRenderer officiel pour tous les types de blocs
          // Layout container unifié: max-width 1200px, centré
          <div 
            className="cms-public-blocks"
            style={{
              maxWidth: '1200px',
              marginLeft: 'auto',
              marginRight: 'auto',
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            <div 
              className="cms-blocks-container"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                width: '100%',
                padding: '24px 16px'
              }}
            >
              {activeBlocks.map((block) => (
                <div 
                  key={block.id} 
                  className="cms-block-wrapper"
                  data-block-id={block.id}
                  data-block-type={block.type}
                  style={{ width: '100%' }}
                >
                  {/* 
                    BlockRenderer gère:
                    - Tous les types de blocs CMS
                    - Fallback gracieux pour types inconnus
                    - External padding configuré dans le bloc
                    - Blocs globaux (global_block_reference)
                  */}
                  <BlockRenderer block={block} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          // Empty state
          <div className="max-w-7xl mx-auto px-4 py-20 text-center">
            <h1 className="text-4xl font-heading font-bold mb-4">{page?.title || 'Page'}</h1>
            <p className="text-muted-foreground">Cette page n'a pas encore de contenu.</p>
          </div>
        )}
      </div>
    </PublicLayout>
  );
};

export default PublicPage;
